<template>
  <div class="modal overflow-auto" select-cy="update-user-details">
    <div class="card inner">
      <div class="header">
        <div class="title">
          <h2>Edit General Information</h2>
        </div>
        <div class="close" @click="close()">
          <i class="icn icn-x sm"></i>
        </div>
      </div>
      <div class="content">
        <form id="user-edit" @submit.prevent @submit="edit">
          <h3 class="sub-title">Details</h3>
          <div class="row mt-2">
            <div class="field">
              <input
                required
                v-model="editItem.firstName"
                type="text"
                placeholder="First name"
                maxlength="127"
                autocomplete="nope"
                data-cy="first"
              />
            </div>
            <div class="field">
              <input
                required
                v-model="editItem.lastName"
                type="text"
                placeholder="Last name"
                maxlength="127"
                autocomplete="nope"
                data-cy="last"
              />
            </div>
          </div>
          <div class="row mt-2">
            <div class="field">
              <input
                required
                v-model="editItem.email"
                type="text"
                pattern="[a-zA-Z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                title="E.g: johndoe@example.com"
                placeholder="Email"
                maxlength="127"
                autocomplete="nope"
                @input="onEmailInput()"
                data-cy="email"
              />
            </div>
          </div>
          <h3 class="sub-title">Company</h3>
          <div class="row mt-2">
            <div class="field">
              <select required v-model="editItem.companyId">
                <option :value="c.id" :selected="c.id === item.companyId" v-for="c of companies" :key="c.id">
                  {{ c.name }}
                </option>
              </select>
            </div>
          </div>
        </form>
      </div>

      <!-- Error Messages -->
      <Alert v-if="errorMessage" :message="errorMessage"></Alert>

      <div class="footer">
        <button class="secondary" type="button" @click="close()">Cancel</button>
        <div class="flex-grow"></div>
        <button type="button" @click="edit()" :disabled="!canSubmit" select-cy="submit-edit-user">
          <span v-if="!isValidating">Save</span>
          <span class="icon" v-if="isValidating">
            <i class="icn icn-refresh sm animate-reverse-spin"></i>
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ALERT_TYPE, MESSAGES } from '@/helpers/constants';
import { MODAL_NAME } from './../store';
import Alert from '@/components/Alert.vue';

export default {
  name: 'UserEditDetails',
  components: {
    Alert,
  },

  data() {
    return {
      editItem: {
        id: null,
        fisrtName: '',
        lastName: '',
        email: '',
      },
      companies: [],
      isValidating: false,
      canCreate: true,
      message: null,
      checkEmail: {
        // eslint-disable-next-line
        emailRegex: /.+\@.+\..+/,
        lastTimestamp: Date.now(),
        isLoading: false,
        timeout: null,
        delay: 300,
      },
    };
  },
  computed: {
    item() {
      return this.$store.getters['users/getUser'];
    },
    canSubmit() {
      return this.canCreate && !this.checkEmail.isLoading;
    },
    errorMessage: {
      get() {
        return this.message;
      },
      set(value) {
        if (value === null) {
          this.message = null;
        } else {
          this.message = {
            content: value,
            type: ALERT_TYPE.ERROR,
          };
        }
      },
    },
  },

  created() {
    this.fetchCompanies();
    this.editItem = { ...this.item };
  },

  methods: {
    close() {
      this.$store.dispatch('users/get', {
        ids: this.item.id,
        includeRoles: true,
        includePermissions: true,
        detailedPermissions: true,
      });
      this.$store.commit('users/close', MODAL_NAME.EDIT_DETAILS);
    },
    fetchItem() {
      this.$store.dispatch('users/get').then(({ data }) => {
        this.companies = data;
        if (data.length > 1) {
          this.item.companyId = data[0].id;
        }
      });
    },
    fetchCompanies() {
      this.$store.dispatch('users/getCompanies').then(({ data }) => {
        this.companies = data;
        if (data.length > 1) {
          this.item.companyId = data[0].id;
        }
      });
    },
    edit() {
      if (this.isValidating) return;
      this.errorMessage = null;
      this.isValidating = true;
      const isValid = document.querySelector('#user-edit').reportValidity();
      if (isValid) {
        // Trim values
        this.editItem.firstName = this.editItem.firstName.trim();
        this.editItem.lastName = this.editItem.lastName.trim();
        this.editItem.email = this.editItem.email.trim();

        this.$store
          .dispatch('users/edit', [this.editItem])
          .then(() => {
            this.close();
          })
          .catch(err => {
            console.error(err);
            if (err && err.status && err.status === 401) {
              this.errorMessage = MESSAGES.NO_PERMISSION;
            } else {
              this.errorMessage = MESSAGES.SERVER_ERROR;
            }
          })
          .finally(() => (this.isValidating = false));
      } else {
        this.isValidating = false;
      }
    },
    onEmailInput() {
      clearTimeout(this.checkEmail.timeout);
      this.checkEmail.timeout = setTimeout(() => {
        this.validateEmail();
      }, this.checkEmail.delay);
    },
    validateEmail() {
      if (this.editItem.email.length > 0 && this.checkEmail.emailRegex.test(this.editItem.email)) {
        let timestamp = Date.now(); // timestamp for this api call
        this.checkEmail.lastTimestamp = timestamp; // 1 , 2 , 3
        this.checkEmail.isLoading = true;
        this.editItem.firstName = this.editItem.firstName.trim();
        this.editItem.lastName = this.editItem.lastName.trim();
        this.$store
          .dispatch('users/get', { key: 'email', value: this.editItem.email })
          .then(({ data }) => {
            if (data && timestamp >= this.checkEmail.lastTimestamp) {
              if (data.length > 0 && data[0].id != this.item.id) {
                this.errorMessage = 'Emails must be unique. A user with the same email already exists.';
                this.canCreate = false;
              } else {
                this.errorMessage = null;
                this.canCreate = true;
              }
            }
          })
          .catch(err => {
            console.error(err);
            // If it fails, let the user free to continue
            this.errorMessage = null;
            this.canCreate = true;
          })
          .finally(() => (this.checkEmail.isLoading = false));
      } else {
        this.errorMessage = null;
        this.canCreate = true;
      }
    },
  },
};
</script>
